import { useCallback, useMemo, useRef, useState } from "react";
import { importPlayers } from "../../../api/index.js";
import ImportCSV from "./ImportCSV.js";
import {
  Box,
  CircularProgress,
  Typography,
  Alert,
  Grid,
  Paper,
  Stack,
  Chip,
  Divider,
  Button,
  Tooltip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";

/** ---------- Helpers UI ---------- */

const normalizeKeys = (row) => {
  const out = {};
  Object.keys(row).forEach((k) => {
    const nk = k.replace(/^\uFEFF/, "").trim();
    const v = row[k];
    out[nk] = typeof v === "string" ? v.trim() : v;
  });
  return out;
};

const ModeChip = ({ mode }) => {
  const color = mode === "created" ? "success" : mode === "updated" ? "info" : "default";
  return <Chip size="small" color={color} label={mode || "-"} />;
};

const StatCard = ({ title, value, hint, tone = "default" }) => (
  <Paper elevation={1} sx={{ p: 2, borderRadius: 3 }}>
    <Stack spacing={0.5}>
      <Typography variant="body2" color="text.secondary">
        {title}
      </Typography>
      <Typography variant="h5" fontWeight={700}>
        {value}
      </Typography>
      {hint && (
        <Typography variant="caption" color={tone === "danger" ? "error.main" : "text.secondary"}>
          {hint}
        </Typography>
      )}
    </Stack>
  </Paper>
);

const SectionTitle = ({ children }) => (
  <Typography variant="h6" sx={{ mt: 3, mb: 1.5 }}>
    {children}
  </Typography>
);

const LookupMiniStat = ({ title, stats }) => (
  <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: "1px dashed #e0e0e0" }}>
    <Typography variant="subtitle2" sx={{ mb: 1 }}>
      {title}
    </Typography>
    <Stack spacing={1}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Chip size="small" variant="outlined" label="found" color="success" />
        <Typography variant="body2">{stats?.found ?? 0}</Typography>
        <Divider flexItem orientation="vertical" sx={{ mx: 1 }} />
        <Chip size="small" variant="outlined" label="notFound" color="error" />
        <Typography variant="body2">{stats?.notFound ?? 0}</Typography>
      </Stack>

      <Stack direction="row" spacing={1} alignItems="center">
        <Chip size="small" label="byThirdParty" variant="outlined" />
        <Typography variant="body2">{stats?.byThirdParty ?? 0}</Typography>
        <Divider flexItem orientation="vertical" sx={{ mx: 1 }} />
        <Chip size="small" label="byName" variant="outlined" />
        <Typography variant="body2">{stats?.byName ?? 0}</Typography>
      </Stack>
    </Stack>
  </Paper>
);

/** ---------- Composant principal ---------- */

const PlayersCSV = () => {
  const [loading, setLoading] = useState(false);
  const [importComplete, setImportComplete] = useState(false);
  const importStartedRef = useRef(false);

  // Erreurs côté client (pré-validation)
  const [clientErrors, setClientErrors] = useState([]);

  // Résultat côté serveur (payload complet)
  const [serverResult, setServerResult] = useState(null);

  // NB: teamEndDate est optionnel en pratique (le back fait teamEndDate && ...)
  const columns = useMemo(
    () => [
      { id: "lastname", label: "last_name" },
      { id: "discipline", label: "discipline" },
      { id: "provider", label: "Groupe" },

      { id: "firstname", label: "first_name", required: false },

      { id: "number", label: "Numero", required: false },
      { id: "birthday", label: "birth_date", required: false },
      { id: "nationality", label: "Nationalité", required: false },
      { id: "size", label: "height", required: false },
      { id: "weight", label: "weight", required: false },
      { id: "positioning", label: "position", required: false },

      { id: "idTeam", label: "team_id", required: false },
      { id: "idPlayer", label: "player_id", required: false },
      { id: "thirdPartyTeam", label: "thirdPartyTeam", required: false },
      { id: "thirdPartyPlayer", label: "thirdPartyPlayer", required: false },

      { id: "teamName", label: "team_name", required: false },
      { id: "teamStartDate", label: "Date début" },
      { id: "teamEndDate", label: "Date fin", required: false },
    ],
    []
  );

  const requiredColumns = useMemo(
    () => columns.filter((c) => c.required !== false).map((c) => c.id),
    [columns]
  );

  const isNonEmpty = (val) => val !== null && val !== undefined && String(val).trim() !== "";

const validateRow = (row) => {
  const missing = requiredColumns.filter((col) => !isNonEmpty(row[col]));

  const hasTeamKeys = isNonEmpty(row.thirdPartyTeam) && isNonEmpty(row.idTeam);
  const hasTeamName = isNonEmpty(row.teamName);

  if (!hasTeamKeys && !hasTeamName) {
    missing.push("team (idTeam+thirdPartyTeam OU teamName)");
  }

  return { ok: missing.length === 0, missing };
};

  const handleDownloadJson = (data) => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `import-players-summary-${new Date().toISOString()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const emptyAgg = () => ({
  message: "Import players (agrégé)",
  importSummary: {
    players: {
      name: "players",
      total: 0,
      successCount: 0,
      errorCount: 0,
      lines: [],
      lookupStats: {
        team: { found: 0, notFound: 0, byThirdParty: 0, byName: 0 },
      },
    },
  },
  details: { errors: [] },
});

const aggRef = useRef(emptyAgg());
const globalIndexRef = useRef(0); // nb de lignes "valids" déjà envoyées (pour décaler les index)

const mergeResults = (agg, data, indexOffset = 0) => {
  const next = structuredClone(agg);

  const p = data?.importSummary?.players;
  const a = next.importSummary.players;

  // Message: tu peux garder le dernier ou mettre un message agrégé
  next.message = "Import players (agrégé)";

  // Compteurs
  a.total += p?.total ?? 0;
  a.successCount += p?.successCount ?? 0;
  a.errorCount += p?.errorCount ?? 0;

  // Lookup stats
  const lt = p?.lookupStats?.team;
  if (lt) {
    a.lookupStats.team.found += lt.found ?? 0;
    a.lookupStats.team.notFound += lt.notFound ?? 0;
    a.lookupStats.team.byThirdParty += lt.byThirdParty ?? 0;
    a.lookupStats.team.byName += lt.byName ?? 0;
  }

  // Lines (décalage index)
  const lines = p?.lines ?? [];
  a.lines.push(
    ...lines.map((l) => ({
      ...l,
      index: (l.index ?? 0) + indexOffset,
    }))
  );

  // Erreurs détaillées (décalage index aussi)
  const errs = data?.details?.errors ?? [];
  next.details.errors.push(
    ...errs.map((e) => ({
      ...e,
      index: (e.index ?? 0) + indexOffset,
    }))
  );

  return next;
};

  const handlePlayerImport = useCallback(async (rows) => {
  // Normaliser les clés reçues (BOM + espaces)
  const normalizedRows = rows.map(normalizeKeys);

  // ✅ Reset UNIQUEMENT au début du 1er batch + aggregation des résultats
   if (!importStartedRef.current) {
    importStartedRef.current = true;
    setLoading(true);
    setImportComplete(false);
    setClientErrors([]);
    setServerResult(null);

    aggRef.current = emptyAgg();
    globalIndexRef.current = 0;
  }

  // 1) Supprimer les lignes totalement vides
  const nonEmptyRows = normalizedRows.filter((row) => Object.values(row).some(isNonEmpty));

  console.log("PlayersCSV debug:", {
    rowsCount: normalizedRows.length,
    nonEmptyCount: nonEmptyRows.length,
    firstRowKeys: nonEmptyRows[0] ? Object.keys(nonEmptyRows[0]) : [],
    firstRow: nonEmptyRows[0] || null,
  });

  // ✅ IMPORTANT : si batch vide → on ignore (ligne fantôme)
  if (nonEmptyRows.length === 0) {
    return;
  }

  // 2) Validation stricte des colonnes obligatoires
  const invalids = [];
  const valids = [];

  nonEmptyRows.forEach((row, idx) => {
    const { ok, missing } = validateRow(row);
    if (!ok) invalids.push({ index: idx + 1, missing, row });
    else valids.push(row);
  });

  if (valids.length === 0) {
    // Là tu peux garder ton setClientErrors détaillé
     const firstRow = nonEmptyRows[0] || normalizedRows[0];

        const missingStats = {};
        invalids.forEach((it) => {
          it.missing.forEach((m) => (missingStats[m] = (missingStats[m] || 0) + 1));
        });

        setClientErrors([
          `❌ Aucune ligne valide après validation.`,
          `Lignes reçues: ${normalizedRows.length}`,
          `Non vides: ${nonEmptyRows.length}`,
          `Valides: ${valids.length}`,
          `Invalides: ${invalids.length}`,
          `Colonnes manquantes (fréquence): ${
            Object.entries(missingStats)
              .sort((a, b) => b[1] - a[1])
              .map(([col, n]) => `${col} (${n})`)
              .join(", ") || "(aucune)"
          }`,
          firstRow ? `Clés détectées 1ère ligne: ${Object.keys(firstRow).join(", ")}` : null,
          ...invalids.slice(0, 20).map((it) => {
            const preview = JSON.stringify(it.row);
            return `Ligne ${it.index} → manquantes: ${it.missing.join(", ")} | row: ${preview}`;
          }),
          invalids.length > 20 ? `… ${invalids.length - 20} autre(s) ligne(s) invalide(s) non affichée(s).` : null,
        ].filter(Boolean));

        setLoading(false);
        setImportComplete(true);
        return;
  }

  if (invalids.length > 0) {
   // accumulation des warnings:
    setClientErrors((prev) => [
      ...prev,
      `⚠️ ${invalids.length} ligne(s) invalide(s) ignorée(s), import des ${valids.length} ligne(s) valides.`,
      ...invalids.slice(0, 20).map((it) => `Ligne ${it.index} → manquantes: ${it.missing.join(", ")}`),
    ]);
  }

  try {
    const { data } = await importPlayers(valids);
     // merge dans l'accu
    const offset = globalIndexRef.current;
    aggRef.current = mergeResults(aggRef.current, data, offset);

    // on incrémente l'offset global avec le nombre de lignes envoyées (valids)
    globalIndexRef.current += valids.length;

    // et on push l'état pour l'UI (affichage "live")
    setServerResult(aggRef.current);
  } catch (error) {
    console.error(error);
    setClientErrors((prev) => [...prev, error?.message ?? "Erreur lors de l'import."]);
  }
}, [requiredColumns]);

const handleImportComplete = () => {
  importStartedRef.current = false;
  setImportComplete(true);
  setLoading(false);
};

  const playersSummary = serverResult?.importSummary?.players;
  const lines = playersSummary?.lines || [];
  const lookupStats = playersSummary?.lookupStats || {};
  const detailsErrors = serverResult?.details?.errors || [];
  const hasServerErrors = (playersSummary?.errorCount || 0) > 0 || detailsErrors.length > 0;
  const clientSeverity = clientErrors?.[0]?.startsWith("⚠️") ? "warning" : "error";

  return (
    <Box>
      <Typography variant="h2" sx={{ mb: 2 }}>
        Joueurs
      </Typography>

      <ImportCSV columns={columns} process={handlePlayerImport} onComplete={handleImportComplete} />

      {loading && (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 2 }}>
          <CircularProgress size={20} />
          <Typography variant="body2">Traitement en cours…</Typography>
        </Stack>
      )}

      {/* Erreurs côté client */}
      {!loading && importComplete && clientErrors.length > 0 && (
      <Alert severity={clientSeverity} sx={{ mt: 2 }}>
          <Stack spacing={1}>
            <strong>
              {clientSeverity === "warning"
                ? "Avertissements CSV (côté client)"
                : "Erreurs de validation CSV (côté client)"}
            </strong>
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {clientErrors.map((e, i) => (
                <li key={i}>
                  <Typography variant="body2" component="span">
                    {e}
                  </Typography>
                </li>
              ))}
            </ul>
          </Stack>
        </Alert>
      )}

      {/* Résultat serveur */}
      {!loading && importComplete && serverResult && (
        <Box sx={{ mt: 2 }}>
          <Alert severity={hasServerErrors ? "warning" : "success"}>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ width: "100%" }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {serverResult.message}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Import (client): {new Date().toLocaleString()}
                </Typography>
              </Box>
              <Stack direction="row" spacing={1}>
                <Tooltip title="Exporter le résumé en JSON">
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<CloudDownloadIcon />}
                    onClick={() => handleDownloadJson(serverResult)}
                  >
                    Export JSON
                  </Button>
                </Tooltip>
              </Stack>
            </Stack>
          </Alert>

          {/* Stats */}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard title="Total lignes" value={playersSummary?.total ?? "-"} hint={playersSummary?.name} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard title="Succès" value={playersSummary?.successCount ?? 0} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Erreurs"
                value={playersSummary?.errorCount ?? 0}
                tone="danger"
                hint={detailsErrors.length ? `${detailsErrors.length} détaillée(s)` : undefined}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard title="Lignes détaillées" value={lines.length} hint="created/updated" />
            </Grid>
          </Grid>

          {/* Lookup stats */}
          <SectionTitle>Résolution des entités (lookup)</SectionTitle>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <LookupMiniStat title="team" stats={lookupStats.team} />
            </Grid>
          </Grid>

          {/* Détail par ligne */}
          <SectionTitle>Détail par ligne</SectionTitle>
          <Paper elevation={0} sx={{ borderRadius: 2, overflow: "hidden", border: "1px solid #eee" }}>
            <Table size="small">
              <TableHead sx={{ backgroundColor: "#fafafa" }}>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Mode</TableCell>
                  <TableCell>Player</TableCell>
                  <TableCell>Team</TableCell>
                  <TableCell>teamKey</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lines.map((line) => (
                  <TableRow key={`${line.index}-${line.playerId || line.playerDbId || Math.random()}`}>
                    <TableCell>{line.index}</TableCell>
                    <TableCell>
                      <ModeChip mode={line.mode} />
                    </TableCell>
                    <TableCell>
                      <Stack spacing={0}>
                        <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                          {line.playerKey || "-"}
                        </Typography>
                        {(line.playerId || line.playerDbId) && (
                          <Typography variant="caption" color="text.secondary">
                            _id: {line.playerId || line.playerDbId}
                          </Typography>
                        )}
                        {line.playerName && (
                          <Typography variant="caption" color="text.secondary">
                            Nom: {line.playerName}
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                        {String(line.teamId || "-")}
                      </Typography>
                      {line.teamName && (
                        <Typography variant="caption" color="text.secondary">
                          {line.teamName}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ fontFamily: "monospace" }}>
                        {line.teamKey || "-"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}

                {lines.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography variant="body2" color="text.secondary">
                        Aucun détail de ligne renvoyé par le serveur.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>

          {/* Erreurs serveur */}
          {hasServerErrors && (
            <>
              <SectionTitle>Erreurs serveur</SectionTitle>
              <Accordion disableGutters>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="body2">{detailsErrors.length} erreur(s) détaillée(s)</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={1}>
                    {detailsErrors.map((e, i) => (
                      <Paper key={i} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                          Ligne {e.index}
                        </Typography>
                        <Typography variant="body2" color="error.main" sx={{ mb: 1 }}>
                          {e.message}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ display: "block", fontFamily: "monospace", whiteSpace: "pre-wrap" }}
                        >
                          {JSON.stringify(e.row, null, 2)}
                        </Typography>
                      </Paper>
                    ))}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            </>
          )}
        </Box>
      )}
    </Box>
  );
};

export default PlayersCSV;
